// findingPresentation — Decision Surface plan, Legibility / altitude pass.
//
// The user-facing layer over the pipeline's finding vocabulary: every
// finding kind maps to a plain-language { title, whyItMatters } the
// architect reads, while the raw pipeline detail (the resolver's
// `message`, `rule_source`, citation ids, `## Asset:` blocks, artifact
// paths, alias arrays) is demoted into a collapsible "developer detail"
// disclosure in each findings panel.
//
// Display-only and safe by plan §3.5: `finding_key` excludes `message`,
// so relabelling copy never invalidates a written waiver. The
// PIPELINE_DENYLIST below is enforced by
// `findingPresentation.test.ts` — no curated user-facing string may
// contain a pipeline token.

import type { AnyFindingKind } from '@/lib/findingSectionMap'

export interface FindingPresentation {
  /** Short, plain-language headline for the finding. */
  title: string
  /** One sentence on why the architect should care — their terms, no
   *  pipeline jargon. */
  whyItMatters: string
}

export const FINDING_PRESENTATION: Record<AnyFindingKind, FindingPresentation> = {
  // --- data dictionary ---
  missing_mandatory_table: {
    title: 'Required table missing',
    whyItMatters:
      'A table your compliance profile requires is absent from the data model, so the document can’t show where that regulated data lives.',
  },
  missing_mandatory_column: {
    title: 'Required column missing',
    whyItMatters:
      'A field your compliance profile mandates (such as a classification or audit column) is missing from this table.',
  },
  retention_tbd: {
    title: 'Retention period not set',
    whyItMatters:
      'This table stores data but has no concrete retention period, so the document would print a placeholder instead of a real policy.',
  },
  retention_below_floor: {
    title: 'Retention shorter than required',
    whyItMatters:
      'The retention period is below the minimum your regulatory profile requires for this kind of data.',
  },
  // --- threat model ---
  missing_mandatory_asset: {
    title: 'Asset missing from the security analysis',
    whyItMatters:
      'An asset your compliance profile expects is not covered by the security threat analysis, leaving a gap an assessor would flag.',
  },
  missing_stride_category: {
    title: 'Threat category not covered',
    whyItMatters:
      'An asset is missing analysis for one of the standard threat categories, so a class of risk is left unexamined.',
  },
  no_asset_sections: {
    title: 'No assets analyzed',
    whyItMatters:
      'The security threat analysis has no assets to examine, so that section of the document would be empty.',
  },
  // --- controls coverage ---
  missing_in_scope_control: {
    title: 'Required control not backed by a requirement',
    whyItMatters:
      'An in-scope regulatory control isn’t tied to any requirement, so the document can’t demonstrate the control is addressed.',
  },
  no_hazard_tables: {
    title: 'No control tables found',
    whyItMatters:
      'The controls-coverage check found no control or hazard tables to verify against your obligations.',
  },
}

/**
 * Pipeline-vocabulary tokens that must never appear in curated
 * user-facing copy (finding titles, why-it-matters, section subtitles).
 * The raw resolver `message` / `rule_source` legitimately use these —
 * they live behind the "developer detail" disclosure, not in the
 * primary copy. Enforced by `findingPresentation.test.ts`.
 */
export const PIPELINE_DENYLIST: readonly string[] = [
  'TBD-shape literal',
  'rule source',
  'rule_source',
  'phase1/',
  'phase2/',
  'phase3/',
  'phase4/',
  '## Asset:',
  'finding_kind',
  'target_content',
  'misfit',
  'pii_tier',
]
